import { z } from 'zod';

/**
 * Entity identifiers are UUIDv7 strings: globally unique, generated without coordination
 * and roughly time-ordered, which keeps database index inserts append-mostly.
 * Each identifier is branded so a PlayerId can never be passed where a SessionId is expected.
 */
/** The person behind one or more characters (ADR 0029). */
export const AccountIdSchema = z.uuid().brand('AccountId');
export type AccountId = z.infer<typeof AccountIdSchema>;

/** A character: the player's identity in the world (holder, owner, combatant). */
export const PlayerIdSchema = z.uuid().brand('PlayerId');
export type PlayerId = z.infer<typeof PlayerIdSchema>;

export const SessionIdSchema = z.uuid().brand('SessionId');
export type SessionId = z.infer<typeof SessionIdSchema>;

export const BuildingIdSchema = z.uuid().brand('BuildingId');
export type BuildingId = z.infer<typeof BuildingIdSchema>;

/** A lot (HOME-CLAIM): a rectangle of the world a character holds, around its hearth. */
export const LotIdSchema = z.uuid().brand('LotId');
export type LotId = z.infer<typeof LotIdSchema>;

export const OfferIdSchema = z.uuid().brand('OfferId');
export type OfferId = z.infer<typeof OfferIdSchema>;

/** Client-generated identifier that makes a command idempotent across retries. */
export const OperationIdSchema = z.uuid().brand('OperationId');
export type OperationId = z.infer<typeof OperationIdSchema>;

/** Correlates logs, HTTP responses and error reports for a single request. */
export const RequestIdSchema = z.string().min(1).max(64).brand('RequestId');
export type RequestId = z.infer<typeof RequestIdSchema>;

const HEX = Array.from({ length: 256 }, (_, i) => i.toString(16).padStart(2, '0'));

/**
 * Generates an RFC 9562 UUIDv7: 48-bit Unix millisecond timestamp, version and variant bits,
 * and 74 random bits. Ordering inside the same millisecond is random, which is acceptable
 * because identifiers are never used to sort events (events carry their own timestamps).
 */
export function uuidv7(nowMs: number = Date.now()): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);

  let timestamp = Math.floor(nowMs);
  for (let i = 5; i >= 0; i--) {
    bytes[i] = timestamp % 256;
    timestamp = Math.floor(timestamp / 256);
  }
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x70;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;

  let out = '';
  for (let i = 0; i < 16; i++) {
    if (i === 4 || i === 6 || i === 8 || i === 10) out += '-';
    out += HEX[bytes[i] ?? 0] ?? '00';
  }
  return out;
}

export const newAccountId = (): AccountId => uuidv7() as AccountId;
export const newPlayerId = (): PlayerId => uuidv7() as PlayerId;
export const newSessionId = (): SessionId => uuidv7() as SessionId;
export const newOperationId = (): OperationId => uuidv7() as OperationId;
export const newBuildingId = (): BuildingId => uuidv7() as BuildingId;
export const newOfferId = (): OfferId => uuidv7() as OfferId;
